import { ConstantsCodeType } from 'src/types/constantsCodes';
import { translateOptions } from 'src/resources/constantsCodes/functions';
import { paidMappingCodes } from './PaymentMethodsTable';
import {
    integrationProductTypeOptions,
    integrationRetinoKeyOptions
} from './integrationMappingDisplayOptions';
import { IntegrationMappingHistoryBlock } from 'src/types/integrationMappingHistory';

export type IntegrationMappingHistoryFormatContext = {
    orderStatuses: ConstantsCodeType[];
    boolNumberCodes: ConstantsCodeType[];
    shipperCodes: ConstantsCodeType[];
    shipperServiceCodes: ConstantsCodeType[];
    paymentMethodCodes: ConstantsCodeType[];
};

const ARROW = ' → ';

const lookupCode = (token: string, options: ConstantsCodeType[]): string => {
    const list = options ?? [];
    const t = token.trim();
    const hit = list.find((o) => String(o.value) === String(t));
    return hit ? String(hit.label) : token;
};

const formatPaidToken = (token: string): string => {
    const opts = translateOptions(paidMappingCodes);
    return lookupCode(token, opts);
};

const formatWithArrow = (value: string, formatPart: (part: string) => string): string => {
    const v = value.trim();
    if (!v.includes(ARROW)) {
        return formatPart(v);
    }
    return v
        .split(ARROW)
        .map((p) => formatPart(p.trim()))
        .join(ARROW);
};

const resolveProductTypePart = (part: string, options: ConstantsCodeType[]): string => {
    const p = part.trim();
    const byValue = options.find((o) => o.value === p);
    if (byValue) {
        return byValue.label;
    }
    const byLabel = options.find((o) => o.label === p);
    if (byLabel) {
        return byLabel.label;
    }
    const byPrefix = options.find((o) => o.label === p || o.label.startsWith(`${p} (`));
    if (byPrefix) {
        return byPrefix.label;
    }
    const lower = p.toLowerCase();
    const byKeyLoose = options.find((o) => String(o.value).toLowerCase() === lower);
    return byKeyLoose ? byKeyLoose.label : part;
};

const formatSummaryProductType = (value: string): string =>
    formatWithArrow(value, (part) => resolveProductTypePart(part, integrationProductTypeOptions));

const isProductCurrencyBlock = (block: IntegrationMappingHistoryBlock): boolean =>
    block.messageKey === 'integrationMappingHistory.currencyExpeditionSet' ||
    block.messageKey === 'integrationMappingHistory.currencyExpeditionEdited';

const isRetinoHistoryBlock = (block: IntegrationMappingHistoryBlock): boolean =>
    block.messageKey.toLowerCase().includes('retino') || block.messageKey === 'integrationMappingHistory.othersDeleted';

export const formatHistoryCellValue = (
    section: 'statuses' | 'shippingMethods' | 'paymentMethods' | 'others',
    block: IntegrationMappingHistoryBlock,
    column: string,
    raw: string,
    ctx: IntegrationMappingHistoryFormatContext
): string => {
    if (raw === '') {
        return '';
    }

    if (section === 'statuses') {
        if (column === 'mysStatus') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.orderStatuses));
        }
        if (column === 'import' || column === 'export') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.boolNumberCodes));
        }
        return raw;
    }

    if (section === 'shippingMethods') {
        if (column === 'shipper') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.shipperCodes));
        }
        if (column === 'serviceCode') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.shipperServiceCodes));
        }
        if (column === 'import') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.boolNumberCodes));
        }
        return raw;
    }

    if (section === 'paymentMethods') {
        if (column === 'paymentMethod') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.paymentMethodCodes));
        }
        if (column === 'paid') {
            return formatWithArrow(raw, formatPaidToken);
        }
        if (column === 'import') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.boolNumberCodes));
        }
        return raw;
    }

    if (section === 'others') {
        if (column === 'summary' && isProductCurrencyBlock(block)) {
            return formatSummaryProductType(raw);
        }
        if (column === 'import' || column === 'export') {
            return formatWithArrow(raw, (p) => lookupCode(p, ctx.boolNumberCodes));
        }
        if (column === 'key' && isRetinoHistoryBlock(block)) {
            return formatWithArrow(raw, (p) => lookupCode(p, integrationRetinoKeyOptions));
        }
    }

    return raw;
};
